import {useTranslation} from '@aboutyou/i18n';
import {styled} from '@linaria/react';
import React from 'react';
import {COLORS} from '../../../constants/colors';
import {SPACING} from '../../../constants/sizing';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {PriceV2} from '../../../types/Price';
import {Button} from '../../atoms/Buttons/Button';
import {
  ContentPopper,
  ContentPopperProps,
} from '../../atoms/ContentPopper/ContentPopper';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {RichText} from '../../atoms/Text/RichText';
import {UnstyledLinkLinaria} from '../../atoms/UnstyledLink/UnstyledLinkLinaria';
import {useLowestProductPriceStateManager} from './useLowestProductPriceStateManager';

export const LowestProductPrice30DMoreInfo: React.FC<{
  showCloseButton: boolean;
  LayoutComponent: ContentPopperProps['LayoutComponent'];
  lpl30d: PriceV2['lpl30d'];
  lowestPrice30DProductStateManagerKey?: string;
}> = ({
  showCloseButton,
  LayoutComponent,
  lpl30d,
  lowestPrice30DProductStateManagerKey,
}) => {
  const {t} = useTranslation();
  const {isOpen, cancel} = useLowestProductPriceStateManager(
    lowestPrice30DProductStateManagerKey,
  );

  const onCloseMoreInfo = () => {
    cancel();
  };

  if (!lpl30d) {
    return null;
  }

  return (
    <ContentPopper
      LayoutComponent={LayoutComponent}
      onClose={onCloseMoreInfo}
      headerContent={lpl30d.legalInfo?.title}
      renderInPortal
      footerContent={
        showCloseButton && (
          <Button
            variant="secondary"
            aria-label={t('Close')}
            onClick={onCloseMoreInfo}
          >
            {t('Close')}
          </Button>
        )
      }
      bodyContent={
        <BodyWrapper>
          {lpl30d.legalInfo?.body.map(item => (
            <BodyItemWrapper key={item.label.text}>
              <RichText>{item.label.text}</RichText>
              <PriceWrapper>
                <RichText>{item.value.text}</RichText>
                {item.vatLabel && (
                  <VatContainer>{item.vatLabel.text}</VatContainer>
                )}
              </PriceWrapper>
            </BodyItemWrapper>
          ))}

          <MoreInfoLink
            aria-label={lpl30d.legalInfo?.link.label}
            to={lpl30d.legalInfo?.link.linkTarget.url || ''}
            rel={'noreferrer'}
          >
            {lpl30d.legalInfo?.link.label}
            <Icon
              asset={GenericIconName.ExternalLink}
              size="SS"
              color={COLORS['Text.Grey']}
            />
          </MoreInfoLink>
        </BodyWrapper>
      }
      open={isOpen}
    />
  );
};

const BodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING['Y.Xl']};
`;

const BodyItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MoreInfoLink = styled(UnstyledLinkLinaria)`
  text-decoration: underline;
  display: flex;
  align-items: center;
  line-height: 1.4;
  gap: ${SPACING['Y.Md']};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  font-size: ${FONT_SIZES.px14};
  padding-block: 14px;
`;

const VatContainer = styled(RichText)`
  font-size: ${FONT_SIZES.px9};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  color: ${COLORS['Text.Grey']};
  line-height: 1.2;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
`;
